import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, Layers, Loader2, PauseCircle, Plus, Printer, RotateCcw, Scale, X } from "lucide-react";
import {
  bundleLabelTsplUrl, getBundleCandidates, getCraneWeight, getInwardInfo, inwardBundle, printTsplViaBridge,
} from "@/lib/api";

const kg = (v) => (v == null ? "—" : Number(v).toLocaleString("en-IN", { maximumFractionDigits: 2 }));

const toRow = (c) => ({
  coil_id: c.coil_id, supplier_coil_id: c.supplier_coil_id || "", sku_name: c.sku_name || c.grade || "",
  thickness_mm: c.thickness_mm, width_mm: c.slit_width_mm || c.width_mm, weight_kg: c.weight_kg,
  expected_gross_kg: Number(c.gross_weight_kg || c.weight_kg || 0),
  sfg_count: Number(c.sfg_count || 1),
});

const CoilPick = ({ row, checked, onToggle, disabled }) => (
  <label data-testid={`bundle-pick-${row.coil_id}`}
    className={`flex items-center gap-3 px-4 py-3 rounded-2xl border-2 cursor-pointer transition-colors ${checked ? "border-indigo-500 bg-indigo-50" : "border-slate-200 bg-white hover:bg-slate-50"} ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}>
    <input type="checkbox" className="h-5 w-5 accent-indigo-600" checked={checked} onChange={onToggle} disabled={disabled} data-testid={`bundle-check-${row.coil_id}`} />
    <div className="min-w-0 flex-1">
      <p className="font-mono-data font-bold text-slate-900">{row.coil_id} <span className="font-sans text-xs font-medium text-slate-500">{row.supplier_coil_id && `· ${row.supplier_coil_id}`}</span></p>
      <p className="text-xs text-slate-500 truncate">{row.sku_name} · {row.thickness_mm ?? "—"} × {row.width_mm ?? "—"} mm · net {kg(row.weight_kg)} kg{row.sfg_count > 1 && <span className="ml-1 font-semibold text-orange-700">· {row.sfg_count} SFGs</span>}</p>
    </div>
    <p className="font-mono-data text-sm font-semibold text-slate-700 whitespace-nowrap">{kg(row.expected_gross_kg)} kg</p>
  </label>
);

export const BundleWeighPanel = ({ startCoilId, onDone, onCancel }) => {
  const [rows, setRows] = useState([]);
  const [tol, setTol] = useState(null);
  const [picked, setPicked] = useState(new Set([startCoilId]));
  const [loading, setLoading] = useState(true);
  const [extraId, setExtraId] = useState("");
  const [reading, setReading] = useState(null);
  const [busy, setBusy] = useState("");
  const [saved, setSaved] = useState(null);

  useEffect(() => {
    getBundleCandidates(startCoilId)
      .then((d) => { setRows(d.coils.map(toRow)); setTol(d.tolerance_kg); })
      .catch((e) => toast.error(e?.response?.data?.detail || "Couldn't load pending coils"))
      .finally(() => setLoading(false));
  }, [startCoilId]);

  const chosen = useMemo(() => rows.filter((r) => picked.has(r.coil_id)), [rows, picked]);
  const expected = useMemo(() => +chosen.reduce((a, r) => a + r.expected_gross_kg, 0).toFixed(2), [chosen]);
  const sfgTotal = useMemo(() => chosen.reduce((a, r) => a + r.sfg_count, 0), [chosen]);
  const locked = saved !== null;

  const toggle = (id) => {
    setReading(null);
    setPicked((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  };

  const addById = async (e) => {
    e.preventDefault();
    const id = extraId.trim().toUpperCase();
    if (!id) return;
    if (rows.some((r) => r.coil_id === id)) { toggle(id); setExtraId(""); return; }
    try {
      const info = await getInwardInfo(id);
      if (info.coil.kind !== "sfg") throw new Error(`${id} is not an SFG`);
      if (info.coil.status !== "pending") throw new Error(`${id} is already ${info.coil.status.replace(/_/g, " ")}`);
      setRows((r) => [...r, toRow(info.coil)]);
      setPicked((s) => new Set([...s, id]));
      setReading(null);
      setExtraId("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || "Coil not found");
    }
  };

  const save = async (weight, hold) => {
    setBusy("save");
    try {
      const b = await inwardBundle(chosen.map((r) => r.coil_id), weight, hold);
      setSaved(b);
      toast.success(`${b.bundle_id}: ${chosen.length} coils ${hold ? "kept as Received – Hold" : "received"}`);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save the bundle");
    } finally { setBusy(""); }
  };

  const weigh = async () => {
    setBusy("weigh");
    setReading(null);
    try {
      const s = await getCraneWeight();
      if (!s.online || s.weight_kg == null) { toast.error(s.hint || "Crane scale is not sending readings"); return; }
      const diff = +(s.weight_kg - expected).toFixed(2);
      const within = Math.abs(diff) <= (tol || 0);
      setReading({ weight: s.weight_kg, diff, within });
      if (within) await save(s.weight_kg, false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't read the crane scale");
    } finally { setBusy((b) => (b === "weigh" ? "" : b)); }
  };

  const printSticker = async () => {
    setBusy("print");
    try { await printTsplViaBridge(bundleLabelTsplUrl(saved.bundle_id)); toast.success("Bundle sticker sent to printer"); }
    catch (e) { toast.error(e?.message?.includes("fetch") ? "Print bridge not running on this PC" : (e?.message || "Print failed")); }
    finally { setBusy(""); }
  };

  return (
    <div data-testid="bundle-panel" className="bg-white rounded-3xl border border-slate-200 p-5 space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-800">
            <Layers className="h-3.5 w-3.5" /> SFG · Mixed bundle
          </span>
          <h3 className="mt-1 text-2xl font-bold tracking-tight text-slate-900">Tick every coil in this bundle</h3>
          <p className="text-sm text-slate-500">Pending SFG from the same packing list. Not listed? Add it by ID below.</p>
        </div>
        {!locked && (
          <button data-testid="bundle-cancel" onClick={onCancel} className="h-11 w-11 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors" title="Cancel">
            <X className="h-5 w-5 text-slate-600" />
          </button>
        )}
      </div>

      {loading ? <Loader2 className="h-6 w-6 animate-spin text-slate-400" /> : (
        <div className="space-y-2 max-h-[340px] overflow-y-auto pr-1" data-testid="bundle-list">
          {rows.map((r) => <CoilPick key={r.coil_id} row={r} checked={picked.has(r.coil_id)} onToggle={() => toggle(r.coil_id)} disabled={locked} />)}
        </div>
      )}

      {!locked && (
        <form onSubmit={addById} className="flex gap-2">
          <input data-testid="bundle-add-input" value={extraId} onChange={(e) => setExtraId(e.target.value.toUpperCase())} placeholder="Add SFG ID from another packing list"
            className="flex-1 h-12 px-4 rounded-2xl border-2 border-slate-300 focus:border-slate-900 outline-none font-mono-data" />
          <button type="submit" data-testid="bundle-add-btn" disabled={!extraId.trim()} className="h-12 px-4 rounded-2xl bg-slate-100 hover:bg-slate-200 font-semibold inline-flex items-center gap-1.5 disabled:opacity-40">
            <Plus className="h-4 w-4" /> Add
          </button>
        </form>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="rounded-2xl bg-slate-50 px-4 py-3">
          <p className="text-sm text-slate-500">Coil IDs ticked</p>
          <p data-testid="bundle-count" className="text-2xl font-bold text-slate-900 font-mono-data">{chosen.length}</p>
        </div>
        <div className="rounded-2xl bg-orange-50 px-4 py-3">
          <p className="text-sm text-slate-500">No of SFGs</p>
          <p data-testid="bundle-sfg-total" className="text-2xl font-bold text-orange-800 font-mono-data">{sfgTotal}</p>
        </div>
        <div className="rounded-2xl bg-slate-50 px-4 py-3">
          <p className="text-sm text-slate-500">Expected bundle gross</p>
          <p data-testid="bundle-expected" className="text-2xl font-bold whitespace-nowrap text-slate-900 font-mono-data">{kg(expected)} <span className="text-lg text-slate-400">kg</span></p>
        </div>
        <div className={`rounded-2xl px-4 py-3 ${tol == null ? "bg-amber-50" : "bg-sky-50"}`}>
          <p className="text-sm text-slate-500">Allowed difference</p>
          <p data-testid="bundle-tolerance" className="text-2xl font-bold whitespace-nowrap text-sky-900 font-mono-data">{tol == null ? "Not set" : `± ${kg(tol)}`} <span className="text-lg text-sky-400">kg</span></p>
        </div>
      </div>

      {!locked && (
        <button data-testid="bundle-weigh-btn" onClick={weigh} disabled={chosen.length < 2 || !!busy}
          className="w-full h-14 rounded-2xl bg-slate-900 text-white text-lg font-semibold inline-flex items-center justify-center gap-3 hover:bg-slate-800 disabled:opacity-50 transition-colors">
          {busy === "weigh" ? <Loader2 className="h-6 w-6 animate-spin" /> : reading ? <RotateCcw className="h-6 w-6" /> : <Scale className="h-6 w-6" />}
          {chosen.length < 2 ? "Tick at least 2 coils" : reading ? "Get bundle weight again" : "Get bundle weight from crane scale"}
        </button>
      )}

      {reading && (
        <div data-testid="bundle-result" data-within={reading.within} className={`rounded-2xl px-5 py-4 border-2 ${reading.within ? "bg-green-50 border-green-500" : "bg-red-50 border-red-500"}`}>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div><p className="text-sm text-slate-600">Crane scale (bundle)</p><p data-testid="bundle-crane" className="text-3xl font-bold font-mono-data">{kg(reading.weight)} kg</p></div>
            <div><p className="text-sm text-slate-600">Difference</p><p data-testid="bundle-diff" className={`text-3xl font-bold font-mono-data ${reading.within ? "text-green-700" : "text-red-700"}`}>{reading.diff > 0 ? "+" : ""}{kg(reading.diff)} kg</p></div>
            <p className={`inline-flex items-center gap-2 text-xl font-bold ${reading.within ? "text-green-700" : "text-red-700"}`}>
              {reading.within ? <CheckCircle2 className="h-7 w-7" /> : <AlertTriangle className="h-7 w-7" />} {reading.within ? "Within tolerance" : "Out of tolerance"}
            </p>
          </div>
          {!reading.within && !locked && (
            <button data-testid="bundle-hold-btn" onClick={() => save(reading.weight, true)} disabled={!!busy}
              className="mt-3 w-full h-14 rounded-2xl bg-red-600 hover:bg-red-700 text-white text-lg font-semibold inline-flex items-center justify-center gap-3 disabled:opacity-60 transition-colors">
              {busy === "save" ? <Loader2 className="h-6 w-6 animate-spin" /> : <PauseCircle className="h-6 w-6" />} Received – Hold (all {chosen.length} coils)
            </button>
          )}
        </div>
      )}

      {locked && (
        <div data-testid="bundle-saved" className={`rounded-2xl px-5 py-4 text-white space-y-3 ${saved.within ? "bg-green-600" : "bg-red-600"}`}>
          <p className="text-base font-semibold flex items-center gap-2">
            {saved.within ? <CheckCircle2 className="h-6 w-6" /> : <PauseCircle className="h-6 w-6" />}
            Bundle <span data-testid="bundle-id" className="font-mono-data">{saved.bundle_id}</span>: {saved.coil_ids.length} coil IDs · {saved.sfg_total} SFGs {saved.within ? "received" : "on Received – Hold. A Director must release the bundle in Inventory."}
          </p>
          <button data-testid="bundle-print-btn" onClick={printSticker} disabled={busy === "print"}
            className="h-11 px-4 rounded-xl bg-white text-slate-900 font-semibold inline-flex items-center gap-2 hover:bg-slate-100 disabled:opacity-60">
            {busy === "print" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />} Print bundle sticker
          </button>
        </div>
      )}
    </div>
  );
};
